import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../slices';
import { 
  fetchPayoutMultipliers, 
  updatePayoutMultiplier, 
  setFilters, 
  clearError, 
  clearSuccess 
} from '../../slices/payoutMultiplier/payoutMultiplierSlice';
import { PayoutMultiplier as PayoutMultiplierType } from '../../slices/payoutMultiplier/payoutMultiplierSlice';
import { RefreshCw, Search, Filter, Check, X } from 'lucide-react';

const PayoutMultiplier = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { payoutMultipliers, pagination, loading, error, success, filters } = useSelector(
    (state: RootState) => state.PayoutMultiplier
  );

  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [editValues, setEditValues] = useState<{ [key: string]: string }>({});
  const [showFilters, setShowFilters] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  // Fetch data on component mount
  useEffect(() => {
    dispatch(fetchPayoutMultipliers({ 
      page: currentPage, 
      search: searchTerm,
      app_id: filters.app_id,
      market_id: filters.market_id,
      bet_type_id: filters.bet_type_id
    }));
  }, [dispatch, currentPage, searchTerm, filters.app_id, filters.market_id, filters.bet_type_id]);

  // Auto-dismiss success/error messages
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => {
        dispatch(clearSuccess());
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [success, dispatch]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        dispatch(clearError());
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error, dispatch]);

  // Initialize editable values whenever list changes
  useEffect(() => {
    if (payoutMultipliers && payoutMultipliers.length) {
      const map: { [key: string]: string } = {};
      payoutMultipliers.forEach(pm => {
        map[pm.id.toString()] = pm.payout_config.multiply_by?.toString() ?? '';
      });
      setEditValues(map);
    }
  }, [payoutMultipliers]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
    dispatch(setFilters({ search: searchTerm }));
  };

  // Debounce search input (smooth UX)
  useEffect(() => {
    const t = setTimeout(() => {
      dispatch(setFilters({ search: searchTerm }));
      setCurrentPage(1);
    }, 400);
    return () => clearTimeout(t);
  }, [searchTerm, dispatch]);

  const handleFilterChange = (key: string, value: string) => {
    dispatch(setFilters({ [key]: value }));
    setCurrentPage(1);
  };

  const handleClearFilters = () => {
    dispatch(setFilters({ search: '', app_id: '', market_id: '', bet_type_id: '' }));
    setSearchTerm('');
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleRefresh = () => {
    dispatch(fetchPayoutMultipliers({ 
      page: currentPage, 
      search: searchTerm,
      app_id: filters.app_id,
      market_id: filters.market_id,
      bet_type_id: filters.bet_type_id
    }));
  };

  const handleEditChange = (id: number, value: string) => {
    setEditValues({ ...editValues, [id.toString()]: value });
  };

  const handleSave = async (id: number) => {
    const raw = editValues[id.toString()];
    const value = parseFloat(raw);
    if (isNaN(value) || value < 0) {
      setLocalError('Please enter a valid positive number');
      return;
    }

    try {
      await dispatch(updatePayoutMultiplier({ 
        id, 
        payout_multiplier: value 
      })).unwrap();
      setLocalError(null);
    } catch (err: any) {
      setLocalError(typeof err === 'string' ? err : 'Failed to update');
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2,
    }).format(amount);
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'running now':
        return 'text-green-600 bg-green-100';
      case 'betting is closed':
        return 'text-red-600 bg-red-100';
      case 'closed for today':
        return 'text-gray-600 bg-gray-100';
      default:
        return 'text-blue-600 bg-orange-100';
    }
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Payout Multipliers</h1>
        <p className="text-gray-600">Manage payout multipliers for different bet types and markets</p>
      </div>

      {/* Success/Error Messages */}
      {(success || error || localError) && (
        <div className="space-y-3 mb-4">
          {success && (
            <div className="flex items-start justify-between p-4 bg-green-50 border border-green-200 text-green-700 rounded">
              <span className="font-medium">{success}</span>
              <button
                onClick={() => dispatch(clearSuccess())}
                className="text-green-700 hover:text-green-800"
                aria-label="Dismiss"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          )}
          {(error || localError) && (
            <div className="flex items-start justify-between p-4 bg-red-50 border border-red-200 text-red-700 rounded">
              <span className="font-medium">{localError || error}</span>
              <button
                onClick={() => { setLocalError(null); dispatch(clearError()); }}
                className="text-red-700 hover:text-red-800"
                aria-label="Dismiss"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      )}

      {/* Search and Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
        <div className="flex flex-col lg:flex-row gap-4">
          {/* Search */}
          <form onSubmit={handleSearch} className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by bet type name..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </form>

          {/* Filter Toggle */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <Filter className="w-4 h-4 mr-2" />
            Filters
          </button>

          {/* Refresh */}
          <button
            onClick={handleRefresh}
            disabled={loading}
            className="flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>

        {/* Advanced Filters */}
        {showFilters && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  App ID
                </label>
                <input
                  type="text"
                  value={filters.app_id}
                  onChange={(e) => handleFilterChange('app_id', e.target.value)}
                  placeholder="Filter by app ID..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Market ID
                </label>
                <input
                  type="text"
                  value={filters.market_id}
                  onChange={(e) => handleFilterChange('market_id', e.target.value)}
                  placeholder="Filter by market ID..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Bet Type ID
                </label>
                <input
                  type="text"
                  value={filters.bet_type_id}
                  onChange={(e) => handleFilterChange('bet_type_id', e.target.value)}
                  placeholder="Filter by bet type ID..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
            <div className="mt-4 flex justify-end">
              <button
                onClick={handleClearFilters}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                Clear Filters
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          <span className="ml-2 text-gray-600">Loading payout multipliers...</span>
        </div>
      )}

      {/* Error State */}
      {error && !loading && (
        <div className="text-center py-8">
          <div className="text-red-500 text-xl mb-4">Error: {error}</div>
          <button
            onClick={handleRefresh}
            className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded"
          >
            Retry
          </button>
        </div>
      )}

      {/* Payout Multipliers List */}
      {!loading && !error && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Payout Multipliers</h3>
          </div>
          
          <div className="divide-y divide-gray-200">
            {payoutMultipliers.map((multiplier) => (
              
              
              <div key={multiplier.id} className="p-4 sm:p-6 hover:bg-gray-50 transition-colors">
                <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 items-center">
                  {/* Name */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Name
                    </label>
                    <div className="text-sm text-gray-900 font-medium">
                      {multiplier.bet_type.name}
                    </div>
                  </div>

                  {/* Game Type */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Game Type
                    </label>
                    <div className="text-sm text-gray-900">
                      {multiplier.bet_type.type}
                    </div>
                  </div>

                  {/* Type */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Type
                    </label>
                    <div className="text-sm text-gray-900">
                      {multiplier.bet_type.type}
                    </div>
                  </div>

                  {/* Multiply By */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Multiply By
                    </label>
                    <div className="flex items-center space-x-2">
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={editValues[multiplier.id.toString()] || ''}
                        onChange={(e) => handleEditChange(multiplier.id, e.target.value)}
                        className="w-full px-3 py-1 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Enter multiplier"
                      />
                      <button
                        onClick={() => handleSave(multiplier.id)}
                        disabled={loading}
                        className="inline-flex items-center px-3 py-1 text-xs font-medium text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-50"
                      >
                        <Check className="w-3 h-3 mr-1" /> Save
                      </button>
                    </div>
                  </div>

                  {/* Save Button */}
                  <div className="hidden lg:flex justify-end" />
                </div>

                {/* Additional Info */}
                <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4 text-xs text-gray-500">
                  <div>
                    <span className="font-medium">Market:</span> {multiplier.market?.name || 'All Markets'}
                    <span className={`ml-2 px-2 py-1 rounded-full text-xs ${getStatusColor(multiplier.market?.current_status || '')}`}>
                      {multiplier.market?.current_status || 'N/A'}
                    </span>
                  </div>
                  <div>
                    <span className="font-medium">Min Amount:</span> {formatCurrency(multiplier.payout_config.min_amount)}
                  </div>
                  <div>
                    <span className="font-medium">Max Amount:</span> {formatCurrency(multiplier.payout_config.max_amount)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Pagination */}
      {pagination && pagination.total_pages > 1 && (
        <div className="mt-6 flex items-center justify-between">
          <div className="text-sm text-gray-700">
            Showing {((pagination.current_page - 1) * pagination.page_size) + 1} to{' '}
            {Math.min(pagination.current_page * pagination.page_size, pagination.total_count)} of{' '}
            {pagination.total_count} results
          </div>
          
          <div className="flex space-x-2">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={!pagination.has_previous}
              className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            
            <span className="px-3 py-2 text-sm font-medium text-gray-700 bg-orange-50 border border-blue-200 rounded-md">
              {pagination.current_page} of {pagination.total_pages}
            </span>
            
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={!pagination.has_next}
              className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PayoutMultiplier;
